/**
 * A configuracao do login pelo IDP, lida do ambiente do build.
 *
 * Os nomes seguem o prefixo `REACT_APP_` do resto do app (`.env.dist`,
 * `vite-env.d.ts` e os workflows de deploy) — o `vite.config.ts` carrega
 * `REACT_APP_` e `VITE_`, e misturar os dois prefixos e como variavel de
 * ambiente some no deploy sem ninguem ver.
 *
 * Nada aqui lanca: configuracao incompleta desliga o botao e deixa um motivo
 * no log. Um `throw` no boot de uma SPA nao protege ninguem — apaga o app
 * inteiro, inclusive o login por senha que continua valendo em `both`.
 */

export type IdpLoginFlag = 'off' | 'both' | 'idp-only';
export type IdpAuthProviderName = 'keycloak' | 'mock';

/** Tem que bater byte a byte com o `redirectUris` do client no realm. */
export const IDP_CALLBACK_PATH = '/auth/callback';
export const SEM_CONTA_PATH = '/sem-conta';

const CLIENT_ID_PADRAO = 'sapron-frontend';

export interface IdpConfig {
  readonly loginFlag: IdpLoginFlag;
  /** O botao "Entrar com Seazone" aparece. */
  readonly enabled: boolean;
  /**
   * O formulario de e-mail e senha aparece. So `idp-only` com o IDP bem
   * configurado o esconde: com `problem`, a tela ficaria sem login nenhum.
   */
  readonly passwordLogin: boolean;
  readonly authProvider: IdpAuthProviderName;
  readonly issuer?: string;
  readonly clientId: string;
  readonly mockSemVinculo: boolean;
  readonly mockAccessTokenTtl?: number;
  /** So com o duble: faz a troca do code ou o refresh falharem de proposito. */
  readonly mockFalha?: 'exchange' | 'refresh';
  /** Por que esta desligado apesar da flag. Vai para o log, nunca para a tela. */
  readonly problem?: string;
}

type Env = Readonly<Record<string, unknown>>;

function text(env: Env, key: string): string | undefined {
  const value = env[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function parseFlag(value: string | undefined): { flag: IdpLoginFlag; problem?: string } {
  if (value === undefined) return { flag: 'off' };
  if (value === 'off' || value === 'both' || value === 'idp-only') return { flag: value };
  // Valor desconhecido nao vira `both` por engano: a flag decide quem ve o
  // login novo, e errar para o lado ligado e o erro caro.
  return { flag: 'off', problem: `REACT_APP_IDP_LOGIN invalido: ${value}` };
}

/**
 * As variaveis que o `readIdpConfig` le, uma a uma. Todas precisam estar em
 * `ENV_PUBLICO` (src/envPublico.ts), a lista do que o build entrega ao
 * navegador; fora dela chegam `undefined`.
 */
export function idpEnvDoBuild(): Env {
  return {
    REACT_APP_IDP_LOGIN: import.meta.env.REACT_APP_IDP_LOGIN,
    REACT_APP_AUTH_PROVIDER: import.meta.env.REACT_APP_AUTH_PROVIDER,
    REACT_APP_IDP_ISSUER: import.meta.env.REACT_APP_IDP_ISSUER,
    REACT_APP_IDP_CLIENT_ID: import.meta.env.REACT_APP_IDP_CLIENT_ID,
    REACT_APP_MOCK_SEM_VINCULO: import.meta.env.REACT_APP_MOCK_SEM_VINCULO,
    REACT_APP_MOCK_ACCESS_TOKEN_TTL: import.meta.env.REACT_APP_MOCK_ACCESS_TOKEN_TTL,
    REACT_APP_MOCK_FALHA: import.meta.env.REACT_APP_MOCK_FALHA,
  };
}

export function readIdpConfig(env: Env): IdpConfig {
  const { flag, problem: problemaDaFlag } = parseFlag(text(env, 'REACT_APP_IDP_LOGIN'));
  const providerBruto = text(env, 'REACT_APP_AUTH_PROVIDER');
  const authProvider: IdpAuthProviderName = providerBruto === 'mock' ? 'mock' : 'keycloak';
  const issuer = text(env, 'REACT_APP_IDP_ISSUER');
  const ttl = Number(text(env, 'REACT_APP_MOCK_ACCESS_TOKEN_TTL'));
  const falha = text(env, 'REACT_APP_MOCK_FALHA');

  const problem =
    problemaDaFlag ??
    (flag !== 'off' && authProvider === 'keycloak' && issuer === undefined
      ? 'REACT_APP_IDP_ISSUER ausente'
      : undefined);

  // Configuracao incompleta vale como `off`, como no backend: o issuer
  // ausente vence a flag, e o formulario de senha continua sendo a porta.
  const enabled = flag !== 'off' && problem === undefined;

  return {
    loginFlag: flag,
    enabled,
    passwordLogin: !(enabled && flag === 'idp-only'),
    authProvider,
    ...(issuer !== undefined && { issuer }),
    clientId: text(env, 'REACT_APP_IDP_CLIENT_ID') ?? CLIENT_ID_PADRAO,
    mockSemVinculo: text(env, 'REACT_APP_MOCK_SEM_VINCULO') === '1',
    ...(Number.isFinite(ttl) && ttl > 0 && { mockAccessTokenTtl: ttl }),
    ...((falha === 'exchange' || falha === 'refresh') && { mockFalha: falha }),
    ...(problem !== undefined && { problem }),
  };
}

export function idpRedirectUri(origin: string): string {
  return `${origin}${IDP_CALLBACK_PATH}`;
}

/** Raiz do app, como o `post.logout.redirect.uris` do client autoriza. */
export function idpPostLogoutRedirectUri(origin: string): string {
  return `${origin}/`;
}

/**
 * Volta do logout federado de quem entrou sem vinculo: a propria `/sem-conta`.
 * O `post.logout.redirect.uris` do client autoriza qualquer caminho do app.
 */
export function idpSemContaLogoutRedirectUri(origin: string): string {
  return `${origin}${SEM_CONTA_PATH}`;
}
